import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionPeriodosGet, apiFacturacionInstance, authHeaderFacturacion } from "../../redux/actions/facturacion/facturacion";

import { Button, Card, Space, Typography, Row, Col, Skeleton, ConfigProvider, DatePicker, Modal, Select, Switch, Empty, Spin } from "antd";
import dayjs from "dayjs";
import { toast } from "../../components/toasts/toastStore";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";
import "./EventoDetallePage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

export default function FacturacionPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.facturacion);
  const navigate = useNavigate();

  const [tab, setTab] = useState("periodos");
  const [activeFilter, setActiveFilter] = useState("todos");

  const [abrirOpen, setAbrirOpen] = useState(false);
  const [rango, setRango] = useState(null);
  const [abriendo, setAbriendo] = useState(false);

  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [guardandoConfig, setGuardandoConfig] = useState(false);
  const [historial, setHistorial] = useState([]);

  useEffect(() => { dispatch(actionPeriodosGet()); }, [dispatch]);

  const fetchConfig = () => {
    setLoadingConfig(true);
    Promise.all([
      apiFacturacionInstance.get("/facturacion/configuracion-fiscal", { headers: authHeaderFacturacion() }),
      apiFacturacionInstance.get("/facturacion/configuracion-fiscal/historial", { headers: authHeaderFacturacion() }),
    ])
      .then(([c, h]) => {
        setConfig(c.data);
        setHistorial(h.data?.items ?? []);
      })
      .catch(() => toast.error("No se pudo cargar la configuracion fiscal"))
      .finally(() => setLoadingConfig(false));
  };

  useEffect(() => {
    if (tab === "config") fetchConfig();
  }, [tab]);

  const counts = useMemo(() => ({
    total: items.length,
    abiertos: items.filter((p) => !p.cerrado).length,
    cerrados: items.filter((p) => p.cerrado).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "abiertos") return items.filter((p) => !p.cerrado);
    if (activeFilter === "cerrados") return items.filter((p) => p.cerrado);
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de periodos", icon: <i className="fa-solid fa-file-invoice-dollar" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "abiertos", label: "Abiertos", icon: <i className="fa-solid fa-lock-open" />, iconCls: "paq-stat-icon-decoracion", count: counts.abiertos },
    { key: "cerrados", label: "Cerrados", icon: <i className="fa-solid fa-lock" />, iconCls: "paq-stat-icon-barra", count: counts.cerrados },
  ];

  const handleAbrir = async () => {
    if (!rango || rango.length !== 2) return toast.warning("Selecciona el rango de fechas del periodo");
    setAbriendo(true);
    try {
      const { data } = await apiFacturacionInstance.post(
        "/facturacion/periodos/abrir",
        { fecha_inicio: rango[0].format("YYYY-MM-DD"), fecha_fin: rango[1].format("YYYY-MM-DD") },
        { headers: authHeaderFacturacion() }
      );
      toast.success("Periodo abierto");
      setAbrirOpen(false);
      setRango(null);
      navigate(`/app/facturacion/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo abrir el periodo");
    } finally {
      setAbriendo(false);
    }
  };

  const handleGuardarConfig = async () => {
    if (!config?.regla_costo) return;
    setGuardandoConfig(true);
    try {
      const { data } = await apiFacturacionInstance.put(
        "/facturacion/configuracion-fiscal",
        { regla_costo: config.regla_costo, es_provisional: Boolean(config.es_provisional) },
        { headers: authHeaderFacturacion() }
      );
      toast.success("Configuracion fiscal actualizada");
      setConfig(data);
      fetchConfig();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setGuardandoConfig(false);
    }
  };

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Facturacion</Title>
                <Text className="paq-subtitle">Periodos de factura global y configuracion fiscal.</Text>
              </Space>

              <div className="cd-header-tabs" style={{ marginTop: 0 }}>
                <button className={`cd-tab-btn ${tab === "periodos" ? "cd-tab-btn-active" : ""}`} onClick={() => setTab("periodos")}>
                  <i className="fa-solid fa-calendar-days" /> Periodos
                </button>
                <button className={`cd-tab-btn ${tab === "config" ? "cd-tab-btn-active" : ""}`} onClick={() => setTab("config")}>
                  <i className="fa-solid fa-gear" /> Configuracion fiscal
                </button>
              </div>
            </div>

            {tab === "periodos" && (
              <>
                <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                  {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                    <Card
                      key={key}
                      className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                      hoverable
                      onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                    >
                      <Space align="center" size={10}>
                        <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                        <div>
                          <div className="paq-stat-value">{count}</div>
                          <div className="paq-stat-label">{label}</div>
                        </div>
                      </Space>
                    </Card>
                  ))}
                </div>

                <div className="paq-expedientes-card">
                  <div className="paq-toolbar">
                    <div className="paq-toolbar-left">
                      <Title level={4} style={{ marginBottom: 0 }}>Periodos ({filteredItems.length})</Title>
                      <Text type="secondary">{filteredItems.length} encontrados</Text>
                    </div>
                    <div className="paq-toolbar-right">
                      <>
                        <Button
                          type="primary"
                          icon={<i className="fa-solid fa-plus" />}
                          onClick={() => setAbrirOpen(true)}
                          style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                        >
                          Abrir periodo
                        </Button>
                      </>
                    </div>
                  </div>

                  {!loading && filteredItems.length === 0 && (
                    <div className="paq-empty">
                      <div style={{ fontSize: 16, fontWeight: 500 }}>Sin periodos que coincidan con los filtros</div>
                    </div>
                  )}

                  <div className="paq-category-block">
                    <div className="paq-grid">
                      {loading && Array.from({ length: 4 }).map((_, i) => (
                        <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                          <div className="paq-card-header">
                            <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                          </div>
                          <div className="paq-card-body">
                            <Skeleton active title={false} paragraph={{ rows: 3 }} />
                          </div>
                        </div>
                      ))}

                      {!loading && filteredItems.length > 0 && (
                        <>
                          {filteredItems.map((p) => (
                            <div key={p.id_periodo_global} className="paq-card">
                              <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                                <i className="fa-solid fa-file-invoice-dollar paq-card-banner-icon" />
                              </div>

                              <div className="paq-card-header">
                                <div className="paq-card-head">
                                  <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>Periodo #{p.id_periodo_global}</Paragraph>
                                  <span className="paq-tipo-badge paq-tipo-sonido">{p.fecha_inicio} — {p.fecha_fin}</span>
                                </div>
                              </div>

                              <div className="paq-card-body">
                                <div className="paq-contenidos-box">
                                  <i className="fa-solid fa-user paq-info-icon" />
                                  <div className="paq-info-text">
                                    <span className="paq-info-label">Abrio</span>
                                    <span className="paq-info-value">{p.creado_por}</span>
                                  </div>
                                </div>
                                {p.folio_fiscal && (
                                  <div className="paq-contenidos-box">
                                    <i className="fa-solid fa-hashtag paq-info-icon paq-info-icon-receta" />
                                    <div className="paq-info-text">
                                      <span className="paq-info-label paq-info-label-receta">Folio fiscal</span>
                                      <span className="paq-info-value">{p.folio_fiscal}</span>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="paq-card-footer">
                                <div className={`paq-bottom-strip ${!p.cerrado ? "is-green" : "is-green"}`}>
                                  <span className="paq-strip-label">{!p.cerrado ? "Abierto" : "Cerrado"}</span>
                                </div>
                                <Button
                                  className="paq-btn-details"
                                  icon={<i className="fa-solid fa-circle-info" />}
                                  onClick={() => navigate(`/app/facturacion/${p.id_periodo_global}`)}
                                >
                                  Ver detalles
                                </Button>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {tab === "config" && (
              <div className="cd-card" style={{ marginTop: 16 }}>
                {loadingConfig ? <Spin /> : (
                  <>
                    <div className="cd-card-header">
                      <div className="cd-card-icon-wrap"><i className="fa-solid fa-gear"></i></div>
                      <h2 className="cd-card-title">Regla fiscal vigente</h2>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 420 }}>
                      <div>
                        <div className="paq-field-label">Regla de costo</div>
                        <Select
                          style={{ width: "100%" }}
                          value={config?.regla_costo}
                          onChange={(v) => setConfig((c) => ({ ...c, regla_costo: v }))}
                          options={[
                            { label: "Sobre subtotal (sin impuesto)", value: "subtotal" },
                            { label: "Sobre total con impuesto", value: "total_con_impuesto" },
                          ]}
                        />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Switch checked={Boolean(config?.es_provisional)} onChange={(v) => setConfig((c) => ({ ...c, es_provisional: v }))} />
                        <span>Regla marcada como provisional</span>
                      </div>
                      <Button
                        type="primary"
                        loading={guardandoConfig}
                        onClick={handleGuardarConfig}
                        style={{ backgroundColor: "#f90336", borderColor: "#f90336", width: 160 }}
                      >
                        Guardar
                      </Button>
                    </div>

                    <div className="cd-card-header" style={{ marginTop: 28 }}>
                      <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
                      <h2 className="cd-card-title">Historial de cambios</h2>
                    </div>
                    {historial.length === 0 ? (
                      <Empty description="Todavia no hay cambios registrados" />
                    ) : (
                      <div className="cd-pagos-list">
                        {historial.map((h) => (
                          <div key={h.id_historial} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                            <div><span className="cd-field-label">Cambio</span><div className="cd-field-value">{h.regla_anterior || "—"} → {h.regla_nueva}</div></div>
                            <div><span className="cd-field-label">Por</span><div className="cd-field-value">{h.user_nombre}</div></div>
                            <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(h.datetime)}</div></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        </div>
      </main>

      <Modal
        open={abrirOpen}
        onCancel={() => setAbrirOpen(false)}
        onOk={handleAbrir}
        okText="Abrir periodo"
        cancelText="Cancelar"
        title="Abrir periodo de facturacion global"
        confirmLoading={abriendo}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <DatePicker.RangePicker style={{ width: "100%" }} format="DD/MM/YYYY" value={rango} onChange={setRango} />
      </Modal>
    </ConfigProvider>
  );
}
